import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import type { TranslationKey } from '../../i18n/types';
import { api } from '../../lib/api';
import { apiErrorMessage } from '../../lib/errors';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FullPageLoader } from '../../components/ui/spinner';

/**
 * Admin crypto console.
 *
 * WHAT IT IS FOR: running the on-chain rail day to day. Everything here comes from
 * the existing `/api/admin/crypto/*` endpoints and nowhere else — the console is a
 * view over the same operations an operator could call by hand, not a second
 * implementation of them.
 *
 * WHAT IT DELIBERATELY DOES NOT DO:
 *
 *   * No "force success". There is no control that marks an invoice paid or a
 *     payout complete without the chain agreeing. The only credit-side actions are
 *     "re-check now" and "run the monitor", both of which simply re-read the chain
 *     and let the existing verification decide.
 *   * No secrets. It never receives or displays a private key, a signer secret or
 *     a webhook secret. It shows whether signing is *configured*, which is an
 *     operational fact, not the credential itself.
 *   * No client-supplied amounts. The import form takes addresses only; every
 *     amount shown is the server's own figure.
 *
 * It does surface the one number that can silently stop sales — the count of
 * available deposit addresses — and says plainly what running out means.
 */

interface CryptoAssetSummary {
  asset: string;
  contract: string;
  decimals: number;
  availableAddresses: number;
}

interface CryptoNetworkSummary {
  network: string;
  label: string;
  assets: CryptoAssetSummary[];
}

interface CryptoSummary {
  networks: CryptoNetworkSummary[];
  unsupportedNetworks: Array<{ network: string; reason: string }>;
  invoices: Array<{ status: string; count: number }>;
  payouts: Array<{ status: string; count: number }>;
  signerConfigured: boolean;
  /** How payouts are signed: in-worker | external-signer | manual-only. */
  signingMode: 'in-worker' | 'external-signer' | 'manual-only';
  payoutAddress: string | null;
  emulator: { enabled: boolean; queued: number };
}

interface PoolAddress {
  address: string;
  network: string;
  asset: string;
  purpose: string;
  status: string;
  label: string | null;
  assignedPaymentId: string | null;
  assignedAt: string | null;
  createdAt: string;
}

interface CryptoInvoiceRow {
  paymentId: string;
  contestId: string;
  contestTitle: string;
  email: string;
  network: string;
  asset: string;
  tokenContract: string;
  depositAddress: string;
  expectedAmount: string;
  receivedAmount: string;
  status: string;
  paymentStatus: string;
  txHash: string | null;
  confirmations: number;
  requiredConfirmations: number;
  detail: string | null;
  expiresAt: string | null;
  createdAt: string;
}

interface ObservedTransfer {
  network: string;
  asset: string;
  txHash: string;
  direction: string;
  from: string;
  to: string;
  amount: string;
  blockNumber: number;
  confirmations: number;
  classification: string;
  paymentId: string | null;
  payoutId: string | null;
  note: string | null;
  firstSeenAt: string;
}

interface ReconciliationAsset {
  network: string;
  asset: string;
  applicationInMinor: number;
  applicationOutMinor: number;
  applicationNetMinor: number;
  observedMinor: number;
  pendingInMinor: number;
  pendingOutMinor: number;
  discrepancyMinor: number;
  unreadableAddresses: number;
  readable: boolean;
}

interface Reconciliation {
  generatedAt: string;
  assets: ReconciliationAsset[];
  unmatchedTransfers: number;
  notes: string[];
}

/** Invoice states that need a human, rather than resolving on their own. */
const ATTENTION_STATUSES = new Set(['underpaid', 'invalid', 'failed']);

/**
 * Human labels for the payout signing mechanism.
 *
 * A static map rather than a template literal so every key stays a literal
 * translation key, which is what keeps the i18n layer type-checked.
 */
const SIGNING_MODE_KEY: Record<
  'in-worker' | 'external-signer' | 'manual-only',
  TranslationKey
> = {
  'in-worker': 'admin.crypto.mode.inWorker',
  'external-signer': 'admin.crypto.mode.externalSigner',
  'manual-only': 'admin.crypto.mode.manualOnly',
};

function tone(status: string): 'neutral' | 'success' | 'warning' | 'danger' | 'brand' {
  if (status === 'confirmed' || status === 'matched') return 'success';
  if (status === 'detected' || status === 'confirming') return 'brand';
  if (status === 'underpaid' || status === 'expired' || status === 'awaiting_payment') {
    return 'warning';
  }
  if (status === 'invalid' || status === 'failed') return 'danger';
  return 'neutral';
}

export default function AdminCryptoPage() {
  const { t } = useTranslation();

  const [summary, setSummary] = useState<CryptoSummary | null>(null);
  const [addresses, setAddresses] = useState<PoolAddress[]>([]);
  const [invoices, setInvoices] = useState<CryptoInvoiceRow[]>([]);
  const [transfers, setTransfers] = useState<ObservedTransfer[]>([]);
  const [reconciliation, setReconciliation] = useState<Reconciliation | null>(null);
  const [enabledRails, setEnabledRails] = useState<string[]>([]);
  const [disabledRails, setDisabledRails] = useState<string[]>([]);

  const [addressFilter, setAddressFilter] = useState<'available' | 'assigned' | 'retired'>(
    'available',
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [importNetwork, setImportNetwork] = useState('tron');
  const [importAsset, setImportAsset] = useState('USDT');
  const [importText, setImportText] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [summaryData, addressData, invoiceData, transferData, recon, providers] =
        await Promise.all([
          api.get<CryptoSummary>('/admin/crypto/summary'),
          api.get<{ addresses: PoolAddress[] }>(
            `/admin/crypto/addresses?status=${addressFilter}&limit=500`,
          ),
          api.get<{ invoices: CryptoInvoiceRow[] }>('/admin/crypto/invoices'),
          api.get<{ transfers: ObservedTransfer[] }>('/admin/crypto/transfers'),
          api.get<Reconciliation>('/admin/crypto/reconciliation'),
          // The rail policy lives on the public providers endpoint; reading it here
          // keeps the console honest about what is actually offered.
          api.get<{ enabledRails: string[]; disabledRails: string[] }>('/contests/providers'),
        ]);

      setSummary(summaryData);
      setAddresses(addressData.addresses);
      setInvoices(invoiceData.invoices);
      setTransfers(transferData.transfers);
      setReconciliation(recon);
      setEnabledRails(providers.enabledRails ?? []);
      setDisabledRails(providers.disabledRails ?? []);
    } catch (caught) {
      setError(apiErrorMessage(caught, t));
    } finally {
      setLoading(false);
    }
  }, [addressFilter, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Decimals per network+asset, taken from the server's own registry. */
  const decimalsFor = useMemo(() => {
    const map = new Map<string, number>();
    for (const network of summary?.networks ?? []) {
      for (const asset of network.assets) {
        map.set(`${network.network}:${asset.asset}`, asset.decimals);
      }
    }
    return (network: string, asset: string): number => map.get(`${network}:${asset}`) ?? 6;
  }, [summary]);

  /** Minor units -> a display amount, using the decimals the server reports. */
  function formatMinor(minor: number, network: string, asset: string): string {
    const decimals = decimalsFor(network, asset);
    const negative = minor < 0;
    const digits = Math.abs(minor).toString().padStart(decimals + 1, '0');
    const whole = digits.slice(0, digits.length - decimals);
    const fraction = decimals > 0 ? `.${digits.slice(digits.length - decimals)}` : '';
    return `${negative ? '-' : ''}${whole}${fraction}`;
  }

  async function runAction(key: string, run: () => Promise<string | null>): Promise<void> {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const message = await run();
      if (message) setNotice(message);
      await load();
    } catch (caught) {
      setError(apiErrorMessage(caught, t));
    } finally {
      setBusy(null);
    }
  }


  async function importAddresses(): Promise<void> {
    // Split on any whitespace or comma, drop blanks and de-duplicate. The server
    // validates every address and rejects the whole request if one is malformed, so
    // a tidy list here saves a round trip.
    const list = Array.from(
      new Set(
        importText
          .split(/[\s,]+/)
          .map((entry) => entry.trim())
          .filter((entry) => entry.length > 0),
      ),
    );
    if (list.length === 0) {
      setError(t('admin.crypto.importEmpty'));
      return;
    }

    await runAction('import', async () => {
      const result = await api.post<{ available: number }>('/admin/crypto/addresses', {
        network: importNetwork,
        asset: importAsset,
        addresses: list,
      });
      setImportText('');
      return t('admin.crypto.imported', {
        count: String(list.length),
        available: String(result.available ?? 0),
      });
    });
  }

  if (loading) return <FullPageLoader label={t('admin.loading')} />;

  const attention = invoices.filter((invoice) => ATTENTION_STATUSES.has(invoice.status));

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-fg">
            {t('admin.crypto.title')}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted">{t('admin.crypto.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void load()}>
            {t('admin.refresh')}
          </Button>
          <Button
            loading={busy === 'monitor'}
            onClick={() =>
              void runAction('monitor', async () => {
                const result = await api.post<{
                  invoices: { examined: number; settled: number };
                }>('/admin/crypto/monitor-run');
                return t('admin.crypto.monitorDone', {
                  examined: String(result.invoices?.examined ?? 0),
                  settled: String(result.invoices?.settled ?? 0),
                });
              })
            }
          >
            {t('admin.crypto.runMonitor')}
          </Button>
        </div>
      </div>

      {error && <Alert tone="error">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {/* --- Operational state ------------------------------------------------ */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex flex-col gap-2 pt-5">
            <span className="text-xs uppercase tracking-wide text-muted">
              {t('admin.crypto.rails')}
            </span>
            <span className="flex flex-wrap gap-1">
              {enabledRails.map((rail) => (
                <Badge key={rail} tone="success">
                  {rail}
                </Badge>
              ))}
              {disabledRails.map((rail) => (
                <Badge key={rail} tone="neutral">
                  {rail} — {t('admin.crypto.railsDisabled')}
                </Badge>
              ))}
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-2 pt-5">
            <span className="text-xs uppercase tracking-wide text-muted">
              {t('admin.crypto.signer')}
            </span>
            <span className="text-sm font-medium text-fg">
              {summary?.signerConfigured
                ? t('admin.crypto.signerReady')
                : t('admin.crypto.signerMissing')}
            </span>
            {summary?.signingMode && (
              <span className="text-xs text-muted">
                {t('admin.crypto.mode')}: {t(SIGNING_MODE_KEY[summary.signingMode])}
              </span>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-2 pt-5">
            <span className="text-xs uppercase tracking-wide text-muted">
              {t('admin.crypto.payoutWallet')}
            </span>
            <span className="break-all font-mono text-xs text-fg">
              {summary?.payoutAddress ?? t('admin.crypto.payoutWalletMissing')}
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-2 pt-5">
            <span className="text-xs uppercase tracking-wide text-muted">
              {t('admin.crypto.emulator')}
            </span>
            <span className="text-sm font-medium text-fg">
              {summary?.emulator.enabled
                ? t('admin.crypto.emulatorOn')
                : t('admin.crypto.emulatorOff')}
            </span>
            {summary?.emulator.enabled && (
              <span className="text-xs text-muted">
                {t('admin.crypto.emulatorQueued', { count: String(summary.emulator.queued) })}
              </span>
            )}
          </CardContent>
        </Card>
      </div>


      {/* --- Networks and the deposit-address pool ---------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.crypto.networks')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {(summary?.networks ?? []).map((network) => (
            <div key={network.network} className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-fg">{network.label}</span>
              <span className="flex flex-wrap gap-2">
                {network.assets.map((asset) => (
                  <Badge
                    key={asset.asset}
                    // The pool running dry is the one number that silently stops
                    // entries, so it is flagged rather than shown as a plain figure.
                    tone={
                      asset.availableAddresses === 0
                        ? 'danger'
                        : asset.availableAddresses < 10
                          ? 'warning'
                          : 'success'
                    }
                  >
                    {asset.asset} · {t('admin.crypto.available')}: {asset.availableAddresses}
                  </Badge>
                ))}
              </span>
            </div>
          ))}

          {(summary?.unsupportedNetworks ?? []).length > 0 && (
            <p className="text-xs text-muted">
              {t('admin.crypto.unsupported')}:{' '}
              {summary?.unsupportedNetworks.map((entry) => entry.network).join(', ')}
            </p>
          )}
        </CardContent>
      </Card>


      {/* --- Address pool management ----------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.crypto.pool')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <Alert tone="info">{t('admin.crypto.poolHint')}</Alert>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex flex-col gap-1">
              <Label htmlFor="pool-network">{t('admin.crypto.importNetwork')}</Label>
              <select
                id="pool-network"
                className="h-9 rounded-md border border-border bg-surface px-3 text-sm text-fg"
                value={importNetwork}
                onChange={(event) => setImportNetwork(event.target.value)}
              >
                <option value="tron">TRON (TRC20)</option>
                <option value="ethereum">Ethereum (ERC20)</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="pool-asset">{t('admin.crypto.importAsset')}</Label>
              <Input
                id="pool-asset"
                value={importAsset}
                onChange={(event) => setImportAsset(event.target.value.toUpperCase())}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="pool-filter">{t('admin.crypto.poolFilter')}</Label>
              <select
                id="pool-filter"
                className="h-9 rounded-md border border-border bg-surface px-3 text-sm text-fg"
                value={addressFilter}
                onChange={(event) =>
                  setAddressFilter(event.target.value as 'available' | 'assigned' | 'retired')
                }
              >
                <option value="available">{t('admin.crypto.poolStatus.available')}</option>
                <option value="assigned">{t('admin.crypto.poolStatus.assigned')}</option>
                <option value="retired">{t('admin.crypto.poolStatus.retired')}</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="pool-addresses">{t('admin.crypto.import')}</Label>
            {/* PUBLIC addresses only. The API validates every line against the
                chain's real address format and rejects the whole request if one is
                wrong, so a private key pasted here by mistake cannot enter the pool. */}
            <textarea
              id="pool-addresses"
              className="min-h-24 w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-fg"
              placeholder={t('admin.crypto.importPlaceholder')}
              value={importText}
              onChange={(event) => setImportText(event.target.value)}
            />
            <p className="text-xs text-muted">{t('admin.crypto.importHint')}</p>
          </div>

          <div>
            <Button loading={busy === 'import'} onClick={() => void importAddresses()}>
              {t('admin.crypto.importButton')}
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="py-2 pe-3">{t('admin.crypto.address')}</th>
                  <th className="py-2 pe-3">{t('admin.crypto.network')}</th>
                  <th className="py-2 pe-3">{t('admin.crypto.status')}</th>
                  <th className="py-2">{t('admin.crypto.assignedTo')}</th>
                </tr>
              </thead>
              <tbody>
                {addresses.slice(0, 50).map((row) => (
                  <tr key={row.address} className="border-t border-border">
                    <td className="break-all py-2 pe-3 font-mono text-xs text-fg">{row.address}</td>
                    <td className="py-2 pe-3 text-muted">
                      {row.network} · {row.asset}
                    </td>
                    <td className="py-2 pe-3">
                      <Badge tone={row.status === 'available' ? 'success' : 'neutral'}>
                        {row.status}
                      </Badge>
                    </td>
                    <td className="break-all py-2 font-mono text-xs text-muted">
                      {row.assignedPaymentId ?? '—'}
                    </td>
                  </tr>
                ))}
                {addresses.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-4 text-sm text-muted">
                      {t('admin.crypto.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>


      {/* --- Payments needing attention --------------------------------------- */}
      {attention.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.crypto.stuck')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {attention.map((invoice) => (
              <div
                key={invoice.paymentId}
                className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 first:border-t-0 first:pt-0"
              >
                <span className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge tone={tone(invoice.status)}>{invoice.status}</Badge>
                  <span className="text-fg">{invoice.email}</span>
                  <span className="text-muted">
                    {invoice.receivedAmount} / {invoice.expectedAmount} {invoice.asset}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  loading={busy === `recheck:${invoice.paymentId}`}
                  onClick={() =>
                    void runAction(`recheck:${invoice.paymentId}`, async () => {
                      await api.post(`/admin/crypto/invoices/${invoice.paymentId}/recheck`);
                      return t('admin.crypto.recheckDone');
                    })
                  }
                >
                  {t('admin.crypto.recheck')}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* --- Invoices --------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.crypto.invoices')}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[52rem] text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="py-2 pe-3">{t('admin.crypto.participant')}</th>
                <th className="py-2 pe-3">{t('admin.crypto.expected')}</th>
                <th className="py-2 pe-3">{t('admin.crypto.received')}</th>
                <th className="py-2 pe-3">{t('admin.crypto.confirmations')}</th>
                <th className="py-2 pe-3">{t('admin.crypto.status')}</th>
                <th className="py-2 pe-3">{t('admin.crypto.depositAddress')}</th>
                <th className="py-2">{t('admin.crypto.txHash')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.paymentId} className="border-t border-border align-top">
                  <td className="py-2 pe-3 text-fg">
                    <div className="flex flex-col">
                      <span>{invoice.email}</span>
                      <span className="text-xs text-muted">{invoice.contestTitle}</span>
                    </div>
                  </td>
                  <td className="py-2 pe-3 text-fg">
                    {invoice.expectedAmount} {invoice.asset}
                    <div className="text-xs text-muted">{invoice.network}</div>
                  </td>
                  <td className="py-2 pe-3 text-fg">{invoice.receivedAmount}</td>
                  <td className="py-2 pe-3 text-fg">
                    {invoice.confirmations} / {invoice.requiredConfirmations}
                  </td>
                  <td className="py-2 pe-3">
                    <Badge tone={tone(invoice.status)}>{invoice.status}</Badge>
                    {invoice.detail && (
                      <div className="mt-1 max-w-[14rem] text-xs text-muted">{invoice.detail}</div>
                    )}
                  </td>
                  <td className="break-all py-2 pe-3 font-mono text-xs text-muted">
                    {invoice.depositAddress}
                  </td>
                  <td className="break-all py-2 font-mono text-xs text-muted">
                    {invoice.txHash ?? '—'}
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-4 text-sm text-muted">
                    {t('admin.crypto.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>


      {/* --- Observed transfers ---------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.crypto.transfers')}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[52rem] text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="py-2 pe-3">{t('admin.crypto.txHash')}</th>
                <th className="py-2 pe-3">{t('admin.crypto.network')}</th>
                <th className="py-2 pe-3">{t('admin.crypto.amount')}</th>
                <th className="py-2 pe-3">{t('admin.crypto.classification')}</th>
                <th className="py-2 pe-3">{t('admin.crypto.confirmations')}</th>
                <th className="py-2">{t('admin.crypto.from')}</th>
              </tr>
            </thead>
            <tbody>
              {transfers.slice(0, 100).map((transfer) => (
                <tr key={transfer.txHash} className="border-t border-border align-top">
                  <td className="break-all py-2 pe-3 font-mono text-xs text-fg">
                    {transfer.txHash}
                  </td>
                  <td className="py-2 pe-3 text-muted">
                    {transfer.network} · {transfer.asset}
                  </td>
                  <td className="py-2 pe-3 text-fg">{transfer.amount}</td>
                  <td className="py-2 pe-3">
                    {/* An unmatched transfer has NOT been credited. Saying so here is
                        the difference between a useful console and a misleading one. */}
                    <Badge tone={tone(transfer.classification)}>{transfer.classification}</Badge>
                    {transfer.note && (
                      <div className="mt-1 max-w-[14rem] text-xs text-muted">{transfer.note}</div>
                    )}
                  </td>
                  <td className="py-2 pe-3 text-fg">{transfer.confirmations}</td>
                  <td className="break-all py-2 font-mono text-xs text-muted">{transfer.from}</td>
                </tr>
              ))}
              {transfers.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-4 text-sm text-muted">
                    {t('admin.crypto.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* --- Reconciliation --------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.crypto.reconciliation')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-xs text-muted">{t('admin.crypto.reconciliationHint')}</p>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="py-2 pe-3">{t('admin.crypto.network')}</th>
                  <th className="py-2 pe-3">{t('admin.crypto.applicationIn')}</th>
                  <th className="py-2 pe-3">{t('admin.crypto.applicationOut')}</th>
                  <th className="py-2 pe-3">{t('admin.crypto.observed')}</th>
                  <th className="py-2">{t('admin.crypto.discrepancy')}</th>
                </tr>
              </thead>
              <tbody>
                {(reconciliation?.assets ?? []).map((row) => (
                  <tr key={`${row.network}:${row.asset}`} className="border-t border-border">
                    <td className="py-2 pe-3 text-fg">
                      {row.network} · {row.asset}
                      {!row.readable && (
                        <div className="text-xs text-muted">{t('admin.crypto.notReadable')}</div>
                      )}
                    </td>
                    <td className="py-2 pe-3 text-fg">
                      {formatMinor(row.applicationInMinor, row.network, row.asset)}
                    </td>
                    <td className="py-2 pe-3 text-fg">
                      {formatMinor(row.applicationOutMinor, row.network, row.asset)}
                    </td>
                    <td className="py-2 pe-3 text-fg">
                      {formatMinor(row.observedMinor, row.network, row.asset)}
                    </td>
                    <td className="py-2">
                      {/* A non-zero discrepancy is reported, never silently corrected. */}
                      <Badge tone={row.discrepancyMinor === 0 ? 'success' : 'danger'}>
                        {formatMinor(row.discrepancyMinor, row.network, row.asset)}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {(reconciliation?.assets ?? []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-4 text-sm text-muted">
                      {t('admin.crypto.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 text-sm">
            <span className="text-fg">
              {t('admin.crypto.unmatched')}: {reconciliation?.unmatchedTransfers ?? 0}
            </span>
            {(reconciliation?.notes ?? []).map((note) => (
              <Alert key={note} tone="info">
                {note}
              </Alert>
            ))}
          </div>

          <p className="text-xs text-muted">
            <Link to="/admin/finance" className="text-brand underline">
              {t('admin.payments.title')}
            </Link>{' '}
            — {t('admin.crypto.payoutsHint')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

